import {DecimalPipe} from '@angular/common';
import {ChangeDetectionStrategy, Component, inject, Input} from '@angular/core';
import moment from 'moment';
import {MarketSeriesManager} from '../market-series-manager';
import {
  EventBank,
  EventMarker,
  MarketSeries,
  MonthCell,
  MonthDot,
  YearRow,
} from '../policy-chart.models';

@Component({
  selector: 'app-fiscal-line-chart',
  standalone: true,
  imports: [DecimalPipe],
  templateUrl: './fiscal-line-chart.component.html',
  styleUrl: './fiscal-line-chart.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FiscalLineChartComponent {
  private readonly seriesManager = inject(MarketSeriesManager);

  @Input({required: true}) year!: YearRow;

  readonly monthLabels = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ];

  /** The manager decides which feeds are drawn; the chart just draws them. */
  get visibleSeries(): MarketSeries[] {
    return this.seriesManager.visibleSeries(this.year);
  }

  visibleDots(month: MonthCell): MonthDot[] {
    return this.seriesManager.visibleDots(month);
  }

  /** Only the primary feed colours its dots by momentum into an FOMC meeting. */
  weekDotClass(series: MarketSeries, index: number): 'normal' | 'positive' | 'negative' {
    if (!series.definition.primary) return 'normal';
    const nextSegment = series.segments[index];
    if (!nextSegment?.preFed) return 'normal';
    return nextSegment.momentum > 0 ? 'positive' : 'negative';
  }

  valueLabel(series: MarketSeries, value: number): string {
    return value.toFixed(series.definition.decimals);
  }

  /** Event ticks for the banks the manager is showing. */
  get visibleEventMarkers(): EventMarker[] {
    return this.year.eventMarkers.filter((marker) =>
      this.seriesManager.showsEvent(marker.bank));
  }

  bankColor(bank: EventBank): string {
    return this.seriesManager.bankColor(bank);
  }

  /** Whether any bank on screen decided a rate outside its calendar that month. */
  hasEmergency(month: number): boolean {
    return this.year.events.some((event) =>
      event.emergency &&
      Number(event.date.slice(5, 7)) === month &&
      this.seriesManager.showsEvent(event.bank));
  }

  /** Pairs a mark with its bank's row in the rate table; see styles.scss. */
  bankClass(bank: EventBank): string {
    return bank.toLowerCase();
  }

  meetingDateLabel(date: string): string {
    return moment(date, 'YYYY-MM-DD', true).format('MMM-DD');
  }
}
